import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Page from '../../../types/shared/Page';
import Studio from '../../../types/studio/Studio';
import StudioWriteDTO from '../../../types/studio/StudioWriteDTO';
import { BaseService } from './base.service';

@Injectable({
    providedIn: 'root'
})
export class StudioService {

    private RESOURCE: string = 'studio';

    constructor(private baseService: BaseService) {
    }

    /**
     * Fetches the studios from the backend.
     *
     * @param search The search query to filter the studios by.
     * @param page The page number to fetch.
     * @param size The number of elements to fetch per page.
     * @return The studios.
     */
    public async getStudios(search?: string, page: number = 0, size: number = 10): Promise<Page<Studio>> {
        let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
        if (search) {
            params = params.set('search', search);
        }

        return await this.baseService.get<Page<Studio>>(this.RESOURCE, params);
    }

    /**
     * Fetches all studios from the backend.
     *
     * @return The studios.
     */
    public async getAllStudios(): Promise<Studio[]> {
        return await this.baseService.get<Studio[]>(this.RESOURCE + '/all');
    }

    /**
     * Fetches a studio from the backend.
     *
     * @param id The ID of the studio to fetch.
     * @return The studio.
     */
    public async getStudio(id: number): Promise<Studio> {
        return await this.baseService.get<Studio>(this.RESOURCE + '/' + id);
    }

    /**
     * Creates a studio in the backend.
     *
     * @param studio The studio to create.
     * @return The response from the backend.
     */
    public async createStudio(studio: StudioWriteDTO): Promise<void> {
        return await this.baseService.post<void>(this.RESOURCE, studio);
    }

    /**
     * Updates a studio in the backend.
     *
     * @param studio The studio to update.
     * @return The response from the backend.
     */
    public async updateStudio(studio: StudioWriteDTO): Promise<void> {
        return await this.baseService.put<void>(this.RESOURCE + '/' + studio.id, studio);
    }

    /**
     * Deletes a studio from the backend.
     *
     * @param id The ID of the studio to delete.
     * @return The response from the backend.
     */
    public async deleteStudio(id: number): Promise<void> {
        return await this.baseService.delete<void>(this.RESOURCE + '/' + id);
    }
}
